import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/shared/PageHero";
import CTASection from "@/components/shared/CTASection";
import PageSEO from "@/components/shared/PageSEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, Cpu, Lightbulb } from "lucide-react";
import { serviceDetails } from "@/data/serviceDetails";

const ServiceDetail = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const service = serviceId ? serviceDetails[serviceId] : null;

  if (!service) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-6 py-32 text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">Service Not Found</h1>
          <p className="text-muted-foreground mb-8">The service you're looking for doesn't exist.</p>
          <Button asChild>
            <Link to="/services">Back to Services</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const truncated = service.description.length > 155
    ? service.description.slice(0, 152) + "..."
    : service.description;

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title={`${service.title} — ${service.subtitle}`.slice(0, 49)}
        description={truncated}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": service.title,
          "serviceType": service.subtitle,
          "description": service.overview || service.description,
          "provider": {
            "@type": "Organization",
            "name": "SDC Gulf",
            "url": "https://sdcgulf.com",
          },
          "areaServed": { "@type": "Country", "name": "Saudi Arabia" },
          "url": `https://sdcgulf.com/services/${serviceId}`,
        }}
      />
      <Navbar />
      
      {/* Back Navigation */}
      <div className="container mx-auto px-6 pt-24">
        <Link 
          to="/services" 
          className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Services
        </Link>
      </div>

      {/* Hero Section */}
      <PageHero
        title={service.title}
        subtitle={service.subtitle}
        description={service.description}
        variant="vibrant"
      />

      {/* Overview Section */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-6">Overview</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {service.overview}
            </p>
          </div>
        </div>
      </section>

      {/* Projects & Initiatives */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Projects & Initiatives</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Real-world implementations demonstrating our expertise and impact
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {service.projects.map((project, index) => (
              <Card key={index} className="bg-card border-border hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
                    <Lightbulb className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">{project.title}</h3>
                  <p className="text-muted-foreground">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies & Methodologies */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Cpu className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold text-foreground">Technologies & Methodologies</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {service.technologies.map((tech, index) => (
                <Badge 
                  key={index} 
                  variant="secondary" 
                  className="px-4 py-2 text-sm bg-primary/10 text-primary border-primary/20"
                >
                  {tech}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-8">Key Benefits</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {service.benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border"
                >
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Ready to Get Started?"
        description="Contact us to discuss how this service can transform your operations."
        primaryButtonText="Request a Quote"
        primaryButtonLink="/contact"
        secondaryButtonText="View All Projects"
        secondaryButtonLink="/projects"
      />

      <Footer />
    </div>
  );
};

export default ServiceDetail;
